import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  effect,
  inject,
  viewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormControlStatus,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  BANK_SELECTOR_FETCH_SUBSTITUTES,
  BANK_SELECTOR_SUBSTITUTE_BANK,
  BankSelectorActions,
  DefaultHedges,
  Entity,
  GroupedPassedItem,
  LegSyncingFields,
  MarketRates,
  MultiHedge,
  PremiumStyle,
  QuotePair,
  RFQ_PROVIDER_STATE_UNQUOTED,
  RFQ_STATUS_PENDING,
  RFQ_STATUS_RFE_PENDING,
  RFQ_STATUS_TRADED,
  RfqSettingsState,
  RfqSettingsStateKey,
  RfqTab,
  SubAccount
} from '@approot/models';
import {
  Account,
  AdminService,
  BookingTradeState,
  ConfigService,
  EntityMapperService,
  HedgeQuoteMessage,
  LegSyncService,
  PricingExtraService,
  ProductsConfigService,
  Provider,
  RfqService,
  RfqTabService,
  SetupEventChange,
  TradesCaptureService,
  UserRfqPreferences,
  UserStorageService
} from '@approot/services';
import { isProductNameATarf } from '@approot/services/rfq/rfq-tab/rfq-tab';
import { valueIncreaseDecreasebg, openClose } from '@approot/shared/animations';
import {
  DateUtilities,
  SubscriptionSink,
  Utilities,
  clone,
  dvSchemas,
  getOppositePosition,
  handleChoicePrice,
  reformatDate,
  shortUuid,
  sleep
} from '@approot/utilities';
import dayjs from 'dayjs';
import { isEqual, maxBy, pick } from 'lodash';
import { Debounce, Throttle } from 'lodash-decorators';
import {
  Observable,
  Subscription,
  combineLatest,
  interval,
  of,
  throwError
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  retry,
  skip,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { RfqMultiHedgeGroupComponent } from '../rfq-multi-hedge-group/rfq-multi-hedge-group.component';
import {
  HedgeRecord,
  IndicativeSpot,
  isQuoteOutdated,
  mapHedgeGroup
} from './rfq-setup';
import { RfqSetupService, defaultPremiumValues } from './rfq-setup.service';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';

const DefaultDisplayOrder = [
  'isBlockTrade',
  'account',
  'subAccount',
  'dealOwner',
  'accountCID',
  'cityLocation',
  'bookingLocation',
  'primeBroker',
  'premium',
  'premiumStyle',
  'indicativePremiumStyle',
  'premiumValue',
  'premiumDate',
  'hedgeTitle',
  'hedgeMode',
  'hedge',
  'execution',
  'spotFwdRef',
  'marketRate',
  'hedgeRate',
  'hedgeRates',
  'hedgeAmount',
  'hedgeCustomAmount',
  'hedgeDirection',
  'hedgeDirectionBid',
  'isDx',
  'banks',
  'isInvestmentAdvice',
  'spots',
  'mifidChecks'
];

type Hedges =
  | 'None'
  | 'Spot'
  | 'Custom Spot'
  | 'Forward'
  | 'Custom Forward'
  | 'NDF'
  | 'Live Auto Hedger'
  | 'Spot Hedge'
  | 'Forward Hedge'
  | 'Custom Hedge';

type SpotPosition = 'spotAsk' | 'spotBid';

const hedgeTypesIfModeIsNone: Hedges[] = ['None'];
const hedgeTypesIfMedusaDeliverable: Hedges[] = ['Forward', 'Spot'];
const hedgeTypesIfMedusaNonDeliverable: Hedges[] = ['NDF'];
const premiumStylesIfPremiumBase: PremiumStyle[] = ['Amount', 'bps', 'percent'];
const premiumStylesIfPremiumTerm: PremiumStyle[] = [
  'Amount',
  'bps',
  'pips',
  'percent'
];
const whiteColor = 'var(--pure-white)';
const greyColor = 'var(--label-grey)';
const orangeColor = 'var(--orange-text-color)';
// DX = Delta Exchange
type HedgeMode = 'Live' | 'None' | 'DX';

@Component({
  selector: 'app-rfq-setup',
  templateUrl: './rfq-setup.component.html',
  styleUrls: ['./rfq-setup.component.scss'],
  animations: [valueIncreaseDecreasebg, openClose]
})
export class RfqSetupComponent implements OnInit, OnDestroy {
  @Input() public blockVolBasedPriceHedgeChanges = false;
  @Input() public bookingState: BookingTradeState;
  @Input() public legSyncingInfo: LegSyncingFields;
  @Input() public pipsHighlightedIndices: number[] = [];
  @Input() public rfqTab: RfqTab;
  @Input() public showBookTrades = false;
  @Output() public premiumChange = new EventEmitter<string>();
  @Output() public hedgeChange = new EventEmitter<DefaultHedges>();
  @Output() public numOfProvidersAvailable = new EventEmitter<number>();
  @Output() public premiumStyleChange = new EventEmitter();
  @Output() public premiumValueChange = new EventEmitter<string>();
  @Output() public providersChange = new EventEmitter<string[]>();
  @ViewChildren(RfqMultiHedgeGroupComponent)
  public multiHedgeGroups: QueryList<RfqMultiHedgeGroupComponent>;
  public accountSet: string;
  public readonly hedges = this.rfqSetupService.hedges;
  public readonly isLegacyWhitelabel: boolean;
  public additionalRfqAttributes = this.rfqSetupService.additionalRfqAttributes;
  public availableAccounts = this.rfqSetupService.availableAccounts;
  public availableSubstitutes: any[];
  public availableSubstitutesShortNames: string[] = [];
  public bankUpdate = new EventEmitter<{ bank: string; status: string }>();
  public brokers: string[] = [];
  public currencyPair: QuotePair;
  public displayOrder: Record<string, number> = {};
  public enableCustomHedgeAmount: boolean;
  public enableMultipleHedges = false;
  public hideMultipleHedges = false;
  public enableSingleHedge = true;
  public enableSingleMedusaHedge: boolean;
  public entities: string[] = [];
  public errorMessage: string = null;
  public form: FormGroup;
  public formValid = false;
  public hedgeModes: HedgeMode[] = ['Live', 'None', 'DX'];
  public identifierWidth: string;
  public ignoreReadOnly = false;
  public indicativeSpots: IndicativeSpot = {
    bid: '',
    ask: '',
    mid: '',
    flashOnChange: false
  };
  public legExpiryDates: string[];
  public legSettlementDates: string[];
  public liveSpotColors = {
    spotAsk: { previous: null, current: whiteColor },
    spotBid: { previous: null, current: whiteColor }
  };
  public loadedRfqSetup: any;
  public optionType: 'Call' | 'Put' = 'Call';
  public premium: GroupedPassedItem[] = [];
  public premiumDateColor = whiteColor;
  public premiumStyles: PremiumStyle[] = [];
  public premiumValues = this.rfqSetupService.premiumValues;
  public productTypesColor = whiteColor;
  public providers: Provider[];
  public rfqRunning = false;
  public settlementDateOfFirstLeg: string;
  public shortDisplayNameWidth: string;
  public showHedgeSection = true;
  public showPanel = this.rfqSetupService.showPanel;
  public showSetupMenu = true;
  public subAccountCID = '';
  public subAccountSelected: SubAccount;
  public subAccounts: SubAccount[] = [];
  public tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  public useBlockTrades = false;
  public whiteLabelPremiumStyles = this.rfqSetupService.whiteLabelPremiumStyles;
  public enableComplianceChecks =
    this.configService.userSettings().parent['rfq:enableComplianceChecks'];
  public uniqueSettlementDates: string[];
  public displayIndicativeSpot = false;
  public latestHedgeQuote: HedgeQuoteMessage;
  public uniqRfqId = shortUuid();

  private useBaseAsDefaultPremiumcurrency =
    this.configService.userSettings().parent[
      'rfq:useBaseAsDefaultPremiumcurrency'
    ];

  private setupSubscriptions = new SubscriptionSink();
  private getLiveSpotSub: Subscription;
  private directionListenedTo = false;
  private useHydraProviders = false;
  private legPremiumValueSub: Subscription;
  private getPremiumSettlementDateSub: Subscription;
  private getSpotFromQuoteSub: Subscription;
  private isVolBasedDevUat =
    this.rfqTabService.tabData.isVolBased &&
    Utilities.currentEnvironment() !== 'prod';
  private latestQuoteEvent$ = toObservable(this.rfqTabService.lastQuoteEvents);
  private previousProvidersEmitted: string[];
  public isWhiteLabelUser = this.adminService.userInfo.isWhiteLabelUser;
  public allEntities: Entity[] = [];
  public filteredEntities: Entity[] = [];
  public allEntitiesSearch: string[] = [];
  public hasDisplayNames = false;
  public showSoleProviderSpot = false;
  public soleProviderSpot = { bid: '', ask: '' };
  private entityMapperService = inject(EntityMapperService);
  private dialog = viewChild<ElementRef<HTMLDialogElement>>('dialog');
  private search = viewChild<ElementRef<HTMLInputElement>>('search');
  public accountSearchState: 'TOO_SHORT' | 'TOO_MANY_RESULTS' | null = null;
  public onPrimeBrokerDisabled: 'HIDE' | 'DISABLED' = 'HIDE';
  public selectedBanksState = this.rfqTabService.selectedBanksState;
  public marketRates: MarketRates;
  private latestUserRfqPreferences: UserRfqPreferences;
  constructor(
    public rfqTabService: RfqTabService,
    private rfqService: RfqService,
    private adminService: AdminService,
    private tradesCaptureService: TradesCaptureService,
    private legSyncService: LegSyncService,
    private pricingExtraService: PricingExtraService,
    private productsConfigService: ProductsConfigService,
    private configService: ConfigService,
    private userStorageService: UserStorageService,
    private rfqSetupService: RfqSetupService
  ) {
    this.isLegacyWhitelabel = this.rfqService.isLegacyWhitelabel();
    this.createDummyForm();
    effect(_ => {
      this.accountSet = this.rfqSetupService.accountSet();
    });

    effect(() => {
      this.rfqRunning = this.rfqTabService.rfqRunning();
      this.ignoreReadOnly = this.rfqTabService.tabData.isVolBased
        ? false
        : this.rfqRunning;
      if (!this.rfqRunning) {
        this.soleProviderSpot.ask = '';
        this.soleProviderSpot.bid = '';
        this.indicativeSpots.ask = '';
        this.indicativeSpots.bid = '';
      }
    });

    effect(() => {
      const lastQuotes = this.rfqTabService.lastQuoteEvents();
      const latestQuotes = Array.from(lastQuotes)[lastQuotes.size - 1]?.[1];
      const cross = this.currencyPair?.base + this.currencyPair?.term;
      const dp = this.productsConfigService.decimalPlacesForCross(cross);
      if (latestQuotes) {
        this.soleProviderSpot.bid =
          latestQuotes.quote.legs[0].spotBid?.toFixed(dp);
        this.soleProviderSpot.ask =
          latestQuotes.quote.legs[0].spotOffer?.toFixed(dp);
      }
    });

    effect(() => {
      this.allEntities = this.entityMapperService.allEntities();

      this.filter('');
      // This is what triggers the modal (vs. the default normal select/dropdown)
      this.hasDisplayNames = this.allEntities.some(
        e => e.shortDisplayName != null
      );

      // Concatenation of the fields so makes it easier to search
      this.allEntitiesSearch = this.allEntities.map(e => {
        return `${e.displayName}${e.displayedIdentifier || e.identifier}${
          e.shortDisplayName
        }`.toLocaleLowerCase();
      });

      // Attempt to calculate the width of the identifier and short display name columns dynamically
      // so there's no annoying resizing of the columns when the filter is applied.

      const maxByIdentifierLength = maxBy(
        this.allEntities,
        e => e.identifier.length
      );
      let maxIdentifierLength = Math.max(
        maxByIdentifierLength?.identifier.length || 0,
        10
      );

      const maxByShortDisplayNameLength = maxBy(
        this.allEntities,
        e => e.shortDisplayName?.length
      );
      let maxShortDisplayNameLength = Math.max(
        maxByShortDisplayNameLength?.shortDisplayName.length || 0,
        10
      );

      this.identifierWidth = `${maxIdentifierLength * 10}px`;
      this.shortDisplayNameWidth = `${maxShortDisplayNameLength * 10}px`;
    });

    effect(() => {
      const hedgeQuoteMessages = this.rfqTabService.hedgeQuoteMessages();
      this.latestHedgeQuote =
        Array.from(hedgeQuoteMessages)[hedgeQuoteMessages.size - 1]?.[1];
      if (this.latestHedgeQuote && this.isGenericMultiHedge()) {
        const cross = this.currencyPair?.base + this.currencyPair?.term;
        const dp = this.productsConfigService.decimalPlacesForCross(cross);
        const spotRates = this.latestHedgeQuote.marketRates.spotRates;
        this.marketRates = {
          hedgeType: this.latestHedgeQuote.hedgeType,
          spotRates: {
            ask: spotRates.ask.toFixed(dp),
            bid: spotRates.bid.toFixed(dp),
            deliveryDate: spotRates.deliveryDate
          },
          forwardRates: this.latestHedgeQuote.marketRates.forwardRates.map(
            forwardRate => {
              return {
                bid: forwardRate.bid.toFixed(dp),
                ask: forwardRate.ask.toFixed(dp),
                mid: ((forwardRate.ask + forwardRate.bid) / 2).toFixed(dp),
                deliveryDate: forwardRate.deliveryDate
              };
            }
          ),
          swapPoints: this.latestHedgeQuote.marketRates.swapPoints.map(
            swapPoint => {
              return {
                bid: swapPoint.bid.toFixed(2),
                ask: swapPoint.ask.toFixed(2),
                deliveryDate: swapPoint.deliveryDate
              };
            }
          )
        };
      }
    });
  }

  public ngOnInit(): void {
    this.useBlockTrades =
      this.rfqTabService.tabData.executionContext.useBlockTrades;

    this.enableCustomHedgeAmount =
      this.rfqTabService.tabData.executionContext.enableCustomHedgeAmount;

    this.setShowIndicativeSpot();
    this.loadSettings();
    this.createForm();
    this.setupDisplayOrder();
    this.setupFormListeners();
    this.setSettings();
    this.subToLegSync();
    this.handleExecutionContext();
    this.rfqSetupService.emptyTaskInputsAddAttr();
    this.rfqSetupService.setup = this;
  }

  public ngOnDestroy(): void {
    this.setupSubscriptions.unsubscribe();
  }

  public selectAccount(entity: Entity): void {
    this.form
      .get('account')
      .setValue(this.entityMapperService.getDisplayEntity(entity.identifier));
    this.dialog().nativeElement.close();
  }

  public openSearchDialog(): void {
    if (!this.hasDisplayNames || this.rfqRunning) {
      return;
    }
    this.search().nativeElement.value = '';
    this.filter('');
    this.dialog().nativeElement.showModal();
  }

  @Debounce(200)
  public filter(event: string): void {
    this.accountSearchState = null;
    if (event.length < 3 && this.allEntities.length > 30) {
      this.filteredEntities = [];
      this.accountSearchState = 'TOO_SHORT';
      return;
    }
    event = event.toLocaleLowerCase().trim();
    if (event === '') {
      this.filteredEntities = this.allEntities;
      return;
    }
    const match = this.allEntitiesSearch.map(e => e.includes(event));
    this.filteredEntities = this.allEntities.filter((e, i) => match[i]);
    if (this.filteredEntities.length > 100) {
      this.accountSearchState = 'TOO_MANY_RESULTS';
      this.filteredEntities = this.filteredEntities.slice(0, 100);
    }
  }

  @Throttle(1000)
  public getAccountTooltip(): string {
    const account = this.form.get('account').value as string;
    const identifier = this.entityMapperService.getRealEntity(account);
    const entity = this.allEntities.find(e => e.identifier === identifier);
    if (entity?.identifier) {
      return [
        entity.displayedIdentifier || entity.identifier,
        entity.shortDisplayName,
        entity.displayName
      ]
        .filter(e => !!e)
        .join('\x0a');
    }
    return account;
  }

  private setupDisplayOrder() {
    const attributeDisplayOrder =
      this.rfqTabService.tabData.executionContext.attributeDisplayOrder ||
      DefaultDisplayOrder;

    const displayOrder = attributeDisplayOrder.reduce((acc, key, idx) => {
      acc[key] = idx;
      return acc;
    }, {});

    const handler = {
      get(obj: Record<string, number>, key: string): number {
        return obj[key] ?? 999;
      }
    };

    this.displayOrder = new Proxy(displayOrder, handler);
  }

  private subToLegSync(): void {
    if (!this.rfqTabService.tabData.isVolBased) {
      return;
    }

    if (this.legSyncingInfo?.premiumValueType) {
      this.setupSubscriptions.sink =
        this.legSyncService.premiumValueTypes$.subscribe(obsArray => {
          this.legPremiumValueSub?.unsubscribe();
          this.legPremiumValueSub = combineLatest(obsArray).subscribe(
            premiumValues => {
              const premiumValueCtrl = this.form.get('premiumValue');
              const leg1PremiumValue = premiumValues[0];
              this.productTypesColor = premiumValues.every(
                k => k === leg1PremiumValue
              )
                ? whiteColor
                : greyColor;

              if (leg1PremiumValue !== premiumValueCtrl.value) {
                premiumValueCtrl.setValue(leg1PremiumValue, {
                  emitEvent: false
                });

                this.formChange(leg1PremiumValue);
              }
            }
          );
          this.setupSubscriptions.sink = this.legPremiumValueSub;
        });
    }

    if (this.legSyncingInfo?.premiumSettlementDate) {
      this.setupSubscriptions.sink =
        this.legSyncService.premiumSettlementDates$.subscribe(
          premiumSettlementDates => {
            this.getPremiumSettlementDateSub?.unsubscribe();
            this.getPremiumSettlementDateSub = combineLatest(
              premiumSettlementDates
            ).subscribe(dates => {
              const premiumDate = this.form.get('premiumDate');
              const leg1PremiumDate = dates[0];
              this.premiumDateColor = dates.every(k => k === leg1PremiumDate)
                ? whiteColor
                : greyColor;

              if (
                leg1PremiumDate !== premiumDate.value &&
                DateUtilities.isPrettyDate(leg1PremiumDate)
              ) {
                setTimeout(() => {
                  /* needed as its possible that clrdate picker,
               when loaded will overwrite the current value */
                  premiumDate.setValue(leg1PremiumDate, { emitEvent: false });
                }, 500);
              }
            });
            this.setupSubscriptions.sink = this.getPremiumSettlementDateSub;
          }
        );
    }
  }

  private loadSettings(): void {
    try {
      const loadedRfqSettings =
        this.userStorageService.getItem<RfqSettingsState>(RfqSettingsStateKey);

      const entity =
        loadedRfqSettings?.['rfq-details-settings']?.[
          this.adminService.medusaAccount.entity
        ];

      this.loadedRfqSetup = this.rfqTab.presetSetupData?.setup || entity || {};
    } catch {}
  }

  private setSettings(): void {
    this.rfqSetupService.setAccountsNames(this.rfqTabService.accounts());
    if (!this.loadedRfqSetup) {
      return;
    }

    const presetSetup = this.rfqTab.presetSetupData?.setup;
    if (presetSetup) {
      Object.keys(presetSetup).forEach(key => {
        this.handleSettingField(key, presetSetup[key]);
      });
    } else {
      ['account', 'hedge', 'premiumValue', 'premium'].forEach(key => {
        const val = this.loadedRfqSetup[key];
        if (key === 'premium') {
          if (this.rfqTab.static) {
            this.handleSettingField(key, this.defaultPremium);
          }
        } else if (key === 'account') {
          this.setDefaultAccount();
        } else if (key === 'hedge') {
          this.enableCustomHedgeAmount =
            this.rfqTabService.tabData.executionContext
              .enableCustomHedgeAmount && val !== 'None';
          this.handleSettingField(key, val);
        } else {
          this.handleSettingField(key, val);
        }
      });
    }
  }

  private handleSettingField(key: string, value: any): void {
    if (!this.form.get(key)) {
      return;
    }

    switch (key) {
      case 'account':
        const account = this.entityMapperService.getDisplayEntity(value);
        if (
          this.availableAccounts().includes(account) ||
          (this.configService.userSettings().isWhiteLabelUser &&
            this.rfqTab.static)
        ) {
          // load saved account if in accounts list
          this.form.get(key).setValue(account);
          this.rfqSetupService.updateAccountSet(account);
        } else if (this.availableAccounts().length > 0) {
          this.rfqSetupService.updateAccountSet(this.availableAccounts()[0]);
          this.form.get(key).setValue(this.rfqSetupService.accountSet());
        }
        break;
      case 'hedge':
        if (this.hedges().includes(value)) {
          this.form.get(key).setValue(value);
        }
        break;
      case 'hedgeMode':
        if (this.enableMultipleHedges) {
          this.form.get(key).setValue(value);
        }
        break;
      case 'banks':
      case 'primeBroker':
        break;
      default:
        this.form.get(key).setValue(value);
        break;
    }
  }

  public setDefaultAccount(): void {
    const accounts = this.availableAccounts();
    if (accounts?.length === 1) {
      this.handleSettingField('account', accounts[0]);
    } else {
      const userPreferencesAccount =
        this.rfqTab.executionContext.userRfqPreferences?.account;
      const accountValue =
        userPreferencesAccount &&
        accounts.includes(
          this.entityMapperService.getDisplayEntity(userPreferencesAccount)
        )
          ? userPreferencesAccount
          : accounts[0];
      this.handleSettingField('account', accountValue);
    }
  }

  public onOptionTypeChange(
    optionType?: 'Call' | 'Put',
    blockSetValue = false
  ): void {
    const legs = this.rfqTabService.tabData.legs;
    this.optionType = optionType || legs[0].leg['optionType'];
    this.initHedgeDirections(blockSetValue);
  }

  public onCurrencyPairChange(blockSetValue: boolean): void {
    const spot = Utilities.cleanFormattedNumeric(
      this.rfqTabService.tabData.legs[0]?.leg.spot
    );
    const spotsCtrl = this.form.get('spots');
    this.removeUnavailableFields(blockSetValue);
    this.setPremiums();
    this.remove3rdCurrency();
    this.currencyPair = this.rfqTabService.tabData.title.quotePair;
    const premium = this.form.get('premium');
    if (!this.premium.includes(premium.value) && !blockSetValue) {
      this.form.get('premium').setValue(this.defaultPremium);
    }
    this.getNewPrimeBrokers(!this.showPanel());
    if (this.form.get('premiumValue').value === 'Spot' && !blockSetValue) {
      this.calculateSpotDate();
    }

    if (
      !this.rfqTab.static &&
      this.rfqTabService.tabData.isVolBased &&
      !blockSetValue
    ) {
      this.setHedgeTypeFromMode('Live');
    }

    if (spot != null && spotsCtrl && !this.showPanel() && !blockSetValue) {
      ['spotAsk', 'spotBid'].forEach((key: SpotPosition) => {
        spotsCtrl.get(key).setValue(spot);
        this.updateSpotColor(key, orangeColor);
      });
    } else {
      this.getLatestSpots(blockSetValue);
    }

    if (this.currencyPair && !this.showPanel()) {
      // only show account in title when panel is visible
      this.updatePanelTitleAccount();
      this.rfqSetupService.defaultIndicativePremiumStyle(
        this.rfqTabService.selectedLegs[0].fieldsToBeEdited.notional !==
          'notional',
        this.rfqTabService.tabData.title.quotePair,
        blockSetValue
      );
      this.rfqSetupService.setShowPanel(true);
    }
  }

  public get defaultPremium(): string {
    const cross = this.currencyPair?.base + this.currencyPair?.term;
    const tabShortName =
      this.rfqTabService.tabData.title.productName?.shortName;
    if (this.useBaseAsDefaultPremiumcurrency) {
      return this.currencyPair?.base;
    } else {
      return tabShortName === 'dcd'
        ? this.depositCurrency()
        : this.productsConfigService.cutOffTimesAndFixingSources?.currencyPairInfo?.find(
            cp => cp.currencyPair === cross
          )?.defaultPremiumCurrency || this.currencyPair?.base;
    }
  }

  public setPremiums() {
    const tabTitle = this.rfqTabService.tabData.title;
    if (!tabTitle.quotePair) {
      this.premium = [];
      return;
    }
    if (tabTitle?.productName?.shortName === 'dcd') {
      this.premium = [{ items: [this.depositCurrency()] }];
    } else {
      this.premium = [
        { items: [tabTitle.quotePair.base, tabTitle.quotePair.term] }
      ];
      if (this.isVolBasedDevUat) {
        this.premium.push({
          group: 'Other',
          items: clone(this.pricingExtraService.currencies).filter(
            x => !this.premium[0].items.includes(x)
          )
        });
      } else {
        if (!this.premium[0].items.includes('USD')) {
          this.premium[0].items.unshift('USD');
        }
      }
    }
  }

  public removeUnavailableFields(blockSetValue = false) {
    const tabData = this.rfqTabService.tabData;
    const tabLegs = tabData.legs;
    const productName = tabData.title.productName?.shortName;

    if (this.rfqTabService.tabData.executionContext.disableForwardPremium) {
      this.rfqSetupService.spotOnlyPremiumValue(blockSetValue);
    }

    switch (productName) {
      case 'ndo':
        this.removeSpots(blockSetValue);
        break;
      case 'ndoSpread':
        this.removeSpots(blockSetValue);
        if (tabLegs[0].leg.settlementDate !== tabLegs[1].leg.settlementDate) {
          this.removeForwards(blockSetValue);
        }
        break;
      case 'dcd':
        this.removeForwards(blockSetValue);
        this.removeSpots(blockSetValue);
        break;
      case 'simpleAccumulator':
        this.removeForwards(blockSetValue);
        break;
      default:
        if (isProductNameATarf(productName)) {
          this.removeForwards(blockSetValue);
        }
        if (
          tabLegs.length > 1 &&
          !tabLegs.every((leg, _, arr) => {
            const leg0 = arr[0].leg;
            const legleg = leg.leg;
            return (
              legleg.expiryDate === leg0.expiryDate &&
              legleg.settlementDate === leg0.settlementDate &&
              legleg.firstFixingDate === leg0.firstFixingDate &&
              legleg.lastFixingDate === leg0.lastFixingDate
            );
          })
        ) {
          this.removeForwards(blockSetValue);
        }
        break;
    }
  }

  public removeSpots(blockSetValue = false): void {
    this.rfqSetupService.updateHedges(
      this.rfqSetupService.defaultHedges.filter(
        x => !x.toLowerCase().includes('spot')
      )
    );

    if (
      !blockSetValue &&
      !this.enableMultipleHedges &&
      this.form.get('hedge').value.toLowerCase().includes('spot')
    ) {
      this.form.get('hedge').setValue('');
    }
  }

  public removeForwards(blockSetValue = false): void {
    this.rfqSetupService.updateHedges(
      this.rfqSetupService.defaultHedges.filter(
        x => !x.toLowerCase().includes('forward')
      )
    );

    if (
      !blockSetValue &&
      !this.enableMultipleHedges &&
      this.form.get('hedge').value.toLowerCase().includes('forward')
    ) {
      this.form.get('hedge').setValue('');
    }
  }

  /**
   * In genericMultiHedge mode, hide a single forward hedge (but needs to be
   * in the DOM to make the API work without extra work)
   */
  public onHedgesChange($event: MultiHedge[]) {
    if (this.isGenericMultiHedge()) {
      const val = this.form.get('hedge').value;
      this.enableMultipleHedges =
        val === 'Forward Hedge' || val === 'Custom Hedge';
      this.hideMultipleHedges =
        this.enableMultipleHedges &&
        $event.length <= 1 &&
        val !== 'Custom Hedge';
    }
  }

  public onPremiumValueChange(val: string) {
    // when the premium value field changes
    if (
      !this.enableMultipleHedges &&
      !this.hedges().includes(this.form.get('hedge').value)
    ) {
      this.form.get('hedge').setValue('');
    }
    this.premiumValueChange.emit(val);
  }

  private isGenericMultiHedge(): boolean {
    return this.rfqTabService.executionContextType() === 'genericMultiHedge';
  }

  private async getNewPrimeBrokers(
    initialSetup: boolean,
    blockSetValue = false
  ): Promise<void> {
    // if its enabled then get new primeBroker other wise it will be None
    if (this.useHydraProviders || !this.currencyPair) {
      return;
    }

    if (!this.rfqTabService.tabData.executionContext.usePrimeBroker) {
      // no prime broker to be selected, just get the providers
      this.getProviders();
      return;
    }
    const formAccount = this.form.get('account').value;

    // There's a possible race condition where maybe this is called before accounts loaded
    // Cannot replicate in dev, so this is just speculative
    let accounts: Account[] = [];
    for (let i = 0; i < 4; i++) {
      accounts = this.rfqTabService.accounts();
      if (accounts.length > 0) {
        break;
      } else {
        await sleep(500);
      }
    }
    if (accounts.length === 0) {
      console.warn('No accounts loaded');
      return;
    }

    let dto = this.rfqTabService
      .accounts()
      .find(
        account =>
          account.name === this.entityMapperService.getRealEntity(formAccount)
      );
    if (dto) {
      dto = pick(dto, ['name', 'shortName', 'id']);
    } else {
      console.warn(
        `No matching account for ${formAccount} found - can't generate dto`
      );
      return;
    }
    const payload = {
      dto,
      quotePair: this.rfqTabService.tabData.title.quotePair
    };
    this.brokers = [];
    const primeBrokerCtrl = this.form.get('primeBroker');

    this.rfqService
      .getAvailableParties(payload)
      .subscribe(
        (response: { result: dvSchemas['AccountAvailableParties'] }) => {
          this.rfqTabService.primeBrokers = response.result;
          if (this.rfqSetupService.shouldMapPrimeBrokersViaEntities) {
            this.brokers = response.result.primeBrokerList.map(pb => {
              return this.rfqSetupService.mapPrimeBrokersViaEntities(
                pb.name,
                pb.shortName
              );
            });
          } else {
            this.brokers = response.result.primeBrokerList.map(
              primeBroker => primeBroker.shortName
            );
          }

          // sort, but ensure "None" is first
          this.brokers = this.brokers.sort((a, b) => {
            if (a === b) {
              return 0;
            }
            if (a === 'None') {
              return -1;
            }
            if (b === 'None') {
              return 1;
            }

            return a.localeCompare(b, 'en', { sensitivity: 'accent' });
          });

          if (
            this.form.get('primeBroker').disabled &&
            !this.useHydraProviders
          ) {
            // no prime broker to be selected, just get the providers
            this.getProviders();
          }

          if (
            !primeBrokerCtrl.enabled ||
            !this.rfqTabService.tabData.executionContext.usePrimeBroker
          ) {
            return;
          }

          const savedPrimeBroker = this.entityMapperService.getDisplayEntity(
            this.loadedRfqSetup?.primeBroker
          );

          if (
            !blockSetValue &&
            initialSetup &&
            savedPrimeBroker &&
            this.brokers.includes(savedPrimeBroker)
          ) {
            // if we are initially setting up the component and the primebroker saved is in the primebrokers list
            this.form.get('primeBroker').setValue(savedPrimeBroker); // set prime broker as what is saved
          } else if (!initialSetup) {
            this.form.get('primeBroker').setValue(this.brokers[0]); // set prime broker as what is saved
          }
        }
      );
  }

  public getPrimeBrokerFromForm() {
    let primeBroker: string = this.form.get('primeBroker').value;
    if (this.rfqSetupService.shouldMapPrimeBrokersViaEntities) {
      const pb = primeBroker;
      primeBroker =
        this.rfqSetupService.reverseMapPrimeBrokersViaEntities(primeBroker);
    }
    return primeBroker;
  }

  public getProviders() {
    if (this.useHydraProviders || !this.rfqTabService.tabData.title.quotePair) {
      return;
    }
    const primeBroker = this.getPrimeBrokerFromForm();

    if (
      this.rfqTabService.primeBrokers &&
      primeBroker &&
      this.form.get('account').value
    ) {
      const formAccount = this.form.get('account').value;
      const primeBrokerDTO =
        this.rfqTabService.primeBrokers.primeBrokerList.find(
          broker =>
            broker.name === primeBroker || broker.shortName === primeBroker
        );
      const payload = {
        primeBrokerDTO,
        accountDTO: this.rfqTabService
          .accounts()
          .find(
            account =>
              account.name ===
              this.entityMapperService.getRealEntity(formAccount)
          ),
        quotePair: {
          base: this.rfqTabService.tabData.title.quotePair.base,
          term: this.rfqTabService.tabData.title.quotePair.term
        }
      };

      const accountAlreadySelected =
        this.latestUserRfqPreferences?.account ===
        this.entityMapperService.getRealEntity(formAccount);
      this.rfqService
        .getEnabledAvailableProviders(payload)
        .pipe(
          tap(resp => {
            if (
              primeBroker === payload.primeBrokerDTO.shortName ||
              primeBroker === payload.primeBrokerDTO.name
            ) {
              this.setProviders(resp.result.mappedProviderList as Provider[]);
            }
          }),
          switchMap(_ =>
            !accountAlreadySelected
              ? this.rfqSetupService.getUserRfqPreferencesByCurrencyPairAndAccount(
                  this.rfqTabService.tabData.title.quotePair.base +
                    this.rfqTabService.tabData.title.quotePair.term,
                  formAccount
                )
              : of(null)
          )
        )
        .subscribe(resp => {
          if (resp) {
            this.latestUserRfqPreferences = resp;
            if (resp.providers && this.providers.length) {
              const selectedProviders = resp.providers.filter(
                preferencesProviders =>
                  this.providers.find(
                    provider =>
                      provider.shortName === preferencesProviders.shortName
                  )
              );
              this.selectedBanksState.update(_ => selectedProviders);
            } else {
              this.keepCheckedProviders();
            }
          } else {
            this.keepCheckedProviders();
          }
        });
    }
  }

  public keepCheckedProviders(): void {
    const selectedProviders = this.selectedBanksState().filter(stateProvider =>
      this.providers.find(
        provider => provider.shortName === stateProvider.shortName
      )
    );
    this.selectedBanksState.update(_ => selectedProviders);
  }

  public hedgeRateStatus(val?: string) {
    val = val || this.form.get('hedge').value;
    if (!this.rfqTabService.tabData.title.quotePair) {
      return;
    }
    this.form.get('hedgeRate').setValue(null);
    const shortName = this.rfqTabService.tabData.title.productName?.shortName;
    if (shortName === 'ndo' || shortName === 'ndoSpread') {
      switch (val) {
        case 'Forward':
        case 'Custom Forward':
          this.form.get('premiumValue').setValue('Forward');
          this.rfqSetupService.setPremiumValues(['Forward']);
          break;
        default:
          this.rfqSetupService.setPremiumValues([...defaultPremiumValues]);
          break;
      }
    }

    switch (val) {
      case 'Custom Spot':
      case 'Custom Forward':
        this.rfqSetupService.enableHedgeRate(this.rfqTab.executionContext);
        if (this.isHedgeCustomAndVolBased) {
          this.form.get('hedgeAmount').enable();
          this.form.get('hedgeDirection').enable();
        }
        break;
      default:
        this.form.get('hedgeAmount').disable();
        this.form.get('hedgeDirection').disable();
        this.form.get('hedgeRate').disable();
        if (!this.isGenericMultiHedge) {
          this.form.get('hedgeRates')?.disable();
        }
        break;
    }
  }

  public bankSelectorResponse(response: BankSelectorActions) {
    switch (response.command) {
      case BANK_SELECTOR_FETCH_SUBSTITUTES:
        this.getAvailableSubstitutes(response.data);
        break;
      case BANK_SELECTOR_SUBSTITUTE_BANK:
        this.substituteBank(response.data);
        break;
    }
  }

  public getAvailableSubstitutes(providerId) {
    // get the other substitutes available
    const requestID = this.rfqTabService.rfqDetailsDTO?.id;

    if (requestID) {
      this.rfqService
        .getAvailableSubstitutes({ requestID })
        .subscribe(response => {
          this.availableSubstitutes = response.result;
          this.availableSubstitutesShortNames = response.result.map(
            provider => provider.shortName
          );
        });
    }
  }

  public substituteBank(substituteData: {
    replacee: string;
    substitute: string;
  }) {
    // substitute one bank for another bank
    const replacee = this.rfqTabService
      .selectedBanksState()
      .find(bank => bank.shortName === substituteData.replacee);
    const replacement = this.availableSubstitutes.find(
      sub => sub.shortName === substituteData.substitute
    );

    this.rfqService
      .substituteProvider({
        providerId: replacement.id,
        quoteId: replacee.quoteId
      })
      .subscribe(response => {
        const tempArray = [...this.rfqTabService.selectedBanksState()];

        const indexOfReplacee = tempArray.findIndex(
          bank => bank.shortName === substituteData.replacee
        );

        tempArray[indexOfReplacee] = {
          // replace selected bank with new bank data
          shortName: response.result.providerShortName,
          id: response.result.providerId,
          status: RFQ_PROVIDER_STATE_UNQUOTED,
          quoteId: response.result.quoteId,
          name: response.result.providerNames[0]
        };

        this.rfqTabService.selectedBanksState.update(_ => tempArray);
        this.rfqTabService.removeQuoteByQuoteId(replacee.quoteId);
      });
  }

  public onSelectSubAccount(index: number) {
    this.subAccountSelected = this.subAccounts[index];
    this.rfqTabService.selectedSubAccount = this.subAccountSelected;
  }

  public isValidQuoteProviderId(providerId: number) {
    const bid = this.rfqTabService.getBidFromProviderId(providerId);
    const offer = this.rfqTabService.getOfferFromProviderId(providerId);
    return (
      (bid &&
        (bid.timeLimit >= this.rfqService.currentTimestamp ||
          !bid.timeLimit)) ||
      (offer &&
        (offer.timeLimit >= this.rfqService.currentTimestamp ||
          offer.timeLimit))
    );
  }

  public get isHedgeCustomAndVolBased() {
    const hedge = this.form.get('hedge').value;
    return (
      (hedge === 'Custom Forward' || hedge === 'Custom Spot') &&
      this.rfqTabService.tabData.isVolBased
    );
  }

  public cancelRfq() {
    // when rfq is cancelled reset the state of all banks
    const banks = this.form.value.banks as string[];
    banks.forEach(bank => {
      this.bankUpdate.emit({
        bank,
        status: RFQ_PROVIDER_STATE_UNQUOTED
      });
    });
    this.rfqTabService.cancelRfq();
  }

  public copyToInternal(target: RfqMultiHedgeGroupComponent, $event: any[]) {
    target.addHedges($event);
  }

  private setMultiHedge(): void {
    if (
      this.rfqTabService.tabData.isVolBased ||
      this.rfqTabService.executionContextType() === 'genericMultiHedge'
    ) {
      setTimeout(() => {
        this.multiHedgeGroups.forEach(x => x.initMultiHedge());
      }, 0);
    }
  }

  public executionOnClick(isDx: boolean): void {
    this.form.get('execution').setValue(isDx ? 'DX' : 'Live');
  }

  public onDatesChange(blockChanges = false): void {
    if (blockChanges) {
      return;
    }
    const isDcd =
      this.rfqTabService.tabData.title.productName?.shortName === 'dcd';

    this.settlementDateOfFirstLeg = isDcd
      ? DateUtilities.dateToPrettyDate(
          this.rfqTabService.tabData.legs[0].childLeg?.settlementDate
        )
      : this.rfqTabService.selectedLegs[0].form.get('settlementDate')?.value;

    this.legSettlementDates = isDcd
      ? this.rfqTabService.tabData.legs.map(x =>
          DateUtilities.dateToPrettyDate(x.childLeg.settlementDate)
        )
      : this.rfqTabService.selectedLegs?.map(
          leg => leg.form.get('settlementDate')?.value
        );

    this.uniqueSettlementDates = [...new Set(this.legSettlementDates || [])];

    this.legExpiryDates = isDcd
      ? this.rfqTabService.tabData.legs.map(x =>
          DateUtilities.dateToPrettyDate(x.childLeg.expiryDate)
        )
      : this.rfqTabService.selectedLegs?.map(
          leg => leg.form.get('expiryDate')?.value
        );

    this.removeUnavailableFields();
    this.setMultiHedge();
    this.enableHedgeRatesForForwardHedge();
    if (this.form.get('premiumValue').value === 'Forward') {
      this.calculateForwardDate();
    }
  }

  private enableHedgeRatesForForwardHedge(): void {
    this.rfqSetupService.enableHedgeRatesForForwardHedge(
      this.rfqTab.executionContext,
      this.legSettlementDates
    );
  }

  public get hedgeMode() {
    return this.form.get('hedgeMode').value;
  }

  public get valid(): boolean {
    if (this.enableMultipleHedges) {
      return this.formValid;
    } else {
      return this.form.valid;
    }
  }

  public showHideSetupMenu(): void {
    this.showSetupMenu = !this.showSetupMenu;
  }

  public removeUSDPremium(): void {
    if (this.isVolBasedDevUat) {
      return;
    }
    const { base, term } = this.rfqTabService.tabData.title.quotePair;
    if (base !== 'USD' && term !== 'USD') {
      const premiumControl = this.rfqSetupService.setupForm.get('premium');
      if (premiumControl.value === 'USD') {
        premiumControl.setValue('');
      }
      const clonedPremium = clone(this.premium);
      this.premium[0].items = clonedPremium[0].items.filter(
        currency => currency !== 'USD'
      );
      this.premium = clonedPremium;
    }
  }

  public remove3rdCurrency(): void {
    if (
      !this.configService.userSettings().parent.disablePremiumInThirdCurrency
    ) {
      return;
    }

    const clonedPremium = clone(this.premium);
    const { base, term } = this.rfqTabService.tabData.title.quotePair;
    clonedPremium[0].items = clonedPremium[0].items.filter(currency => {
      return [base, term].includes(currency);
    });
    this.premium = clonedPremium;
  }

  public spotFieldEdited(field: SpotPosition): void {
    this.updateSpotColor(field, orangeColor);
    if (this.form.get('spots').valid) {
      this.rfqTabService.handleClientQuoteEventUsingLast('spots');
    }
  }

  public getLatestSpots(blockSetValue = false): void {
    const cross = this.currencyPair?.base + this.currencyPair?.term;
    this.getSpotFromQuoteSub?.unsubscribe();
    if (
      this.displayIndicativeSpot ||
      this.rfqTabService.executionContextType() === 'genericMultiHedge'
    ) {
      this.indicativeSpots.flashOnChange =
        this.adminService.userInfo.isWhiteLabelUser;
      const dp = this.productsConfigService.decimalPlacesForCross(cross);
      this.getSpotFromQuoteSub = this.latestQuoteEvent$.subscribe(
        lastQuotes => {
          const latestQuotes = Array.from(lastQuotes)[lastQuotes.size - 1]?.[1];
          if (latestQuotes && this.rfqRunning) {
            this.indicativeSpots.bid =
              latestQuotes.quote.legs[0].spotBid?.toFixed(dp);
            this.indicativeSpots.ask =
              latestQuotes.quote.legs[0].spotOffer?.toFixed(dp);
            this.indicativeSpots.mid = (
              (+this.indicativeSpots.bid + +this.indicativeSpots.ask) /
              2
            ).toFixed(dp);
            if (
              this.rfqTabService.executionContextType() === 'genericMultiHedge'
            ) {
              this.rfqSetupService.updateStreamedSpotRefs(
                latestQuotes.quote.legs[0],
                dp
              );
            }
          }
        }
      );
      this.setupSubscriptions.sink = this.getSpotFromQuoteSub;
    }

    if (
      this.rfqTabService.tabData.executionContext.useSpotInRfqPanel &&
      cross &&
      !blockSetValue
    ) {
      this.form.get('spots').markAsPending();
      this.getLiveSpotSub?.unsubscribe();
      this.getLiveSpotSub = this.rfqService
        .getLiveSpot({ task: 'getLiveSpot', cross })
        .subscribe({
          next: response => {
            const dp = response.nDecimals;
            const spotAsk = response.spotAsk.toFixed(dp);
            const spotBid = response.spotBid.toFixed(dp);
            this.form.get('spots').get('spotBid').setValue(spotBid);
            this.form.get('spots').get('spotAsk').setValue(spotAsk);
            this.updateSpotColor('spotAsk', whiteColor);
            this.updateSpotColor('spotBid', whiteColor);
            this.rfqTabService.handleClientQuoteEventUsingLast('spots');
          }
        });
    }
  }

  public multiHedgesValid(): boolean {
    if (this.enableMultipleHedges) {
      const groups = this.multiHedgeGroups.toArray();
      if (groups.length > 0 && groups.some(group => !group.valid())) {
        return false;
      }
    }
    return true;
  }

  // ************** PRIVATE *************** //

  private canSendRFQPreCheck(): boolean {
    if (!this.form.valid) {
      return false;
    }
    return this.multiHedgesValid();
  }

  private startCanSendRFQCheck() {
    this.setupSubscriptions.sink = interval(500)
      .pipe(
        map(() => {
          const check = this.canSendRFQPreCheck();
          if (!check) {
            this.formValid = false;
            this.errorMessage = null;
          }
          return { check, hedges: this.getHedges() };
        }),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        filter(v => v.check === true),
        switchMap(v => this.canSendRFQPostCheck([], v.hedges.hedges)),
        catchError(e => {
          console.error('startCanSendRFQCheck', e);
          this.formValid = false;
          return throwError(e);
        }),
        retry()
      )
      .subscribe(v => {
        this.formValid = v;
      });
  }

  private canSendRFQPostCheck(
    clientHedges: HedgeRecord[],
    hedges: HedgeRecord[]
  ): Observable<boolean> {
    if (
      !this.enableMultipleHedges ||
      this.rfqTabService.executionContextType() === 'genericMultiHedge'
    ) {
      return of(true);
    }
    this.errorMessage = null;
    if (clientHedges.length === 0 && hedges.length === 0) {
      return of(true);
    }
    return this.rfqTabService
      .validateNewVolBasedQuote(hedges, this.form.get('hedgeMode').value)
      .pipe(
        tap(v => {
          if (!v.msg) {
            return;
          }
          this.errorMessage = v.msg;
        }),
        map(v => !!v.isAdmissible),
        catchError(() => of(false))
      );
  }

  public handleExecutionContext(): void {
    const executionContext = this.rfqTabService.tabData.executionContext;
    this.useHydraProviders =
      executionContext.enableProviderSelectionproviders === false;
    if (this.useHydraProviders) {
      this.setProviders(executionContext.providers);
    } else {
      this.getProviders();
    }

    if (this.rfqTabService.executionContextType() !== 'genericMultiHedge') {
      this.enableMultipleHedges = executionContext.enableMultipleHedges;
    }
    this.enableSingleMedusaHedge = executionContext.enableSingleMedusaHedge;

    if (
      !executionContext.enableMultipleHedges &&
      !executionContext.enableSingleMedusaHedge
    ) {
      this.enableSingleHedge = false;
      this.form.get('hedge').setValue('None');
    }

    if (!executionContext.usePrimeBroker) {
      this.form
        .get('primeBroker')
        .setValue(executionContext.primeBrokerDTO.shortName);
      this.rfqTabService.primeBrokers = {
        primeBrokerList: [executionContext.primeBrokerDTO]
      };
      this.form.get('primeBroker').disable({ emitEvent: false });
    }

    if (executionContext.enableMultipleHedges) {
      this.getEntities();
    }
    if (this.rfqTabService.executionContextType() === 'genericMultiHedge') {
      this.startCanSendRFQCheck();
    }
  }

  public setLegsPremiumDates(targetDate?: string): void {
    if (this.legSyncingInfo?.premiumSettlementDate) {
      const formattedDate = reformatDate(targetDate, 'D MMM YYYY');
      const types = this.legSyncService.premiumValueTypes$.value.map(
        x => x.value
      );
      const premiumValue = this.form.get('premiumValue').value;
      this.legSyncService.premiumSettlementDates$.value.forEach(
        (dateObs, index: number) => {
          if (types[index] === premiumValue) {
            dateObs.next(formattedDate);
          }
        }
      );
    }
  }

  public changeInPremiumStyle(userSelected: boolean): void {
    if (userSelected) {
      this.premiumStyleChange.emit();
    }
  }

  @Throttle(250)
  public canUpdateHedgeCustomValue() {
    const status = this.rfqTabService.tabData.title.status.statusType;
    return !(
      status === RFQ_STATUS_RFE_PENDING ||
      status === RFQ_STATUS_PENDING ||
      status === RFQ_STATUS_TRADED
    );
  }

  public hedgeRatesBlur(field: 'hedgeRateAsk' | 'hedgeRateBid'): void {
    const ctrl = this.form.get('hedgeRates').get(field);
    const value = ctrl.value;
    if (ctrl.value) {
      const cross = this.currencyPair.base + this.currencyPair.term;
      ctrl.setValue(
        Utilities.roundNumberToDp(
          Utilities.formattedNumberToNumber(value),
          this.productsConfigService.decimalPlacesForCross(cross),
          true
        )
      );
    }

    if (this.form.get('hedgeRates').valid) {
      this.submitSetupEvent('hedgeRate');
    }
  }

  public submitSetupEvent(change: SetupEventChange) {
    const requestID = this.rfqTabService.rfqDetailsDTO?.id;
    if (!requestID) {
      return;
    }
    this.rfqTabService.submitSetupEvent(requestID, { change });
  }

  private getEntities() {
    this.tradesCaptureService.getEntities().subscribe({
      next: (entities: Entity[]) => {
        this.entities = entities.map(e => e.identifier);
        let val = this.loadedRfqSetup.entity || '';
        val = val === '-' ? '' : val;
        this.rfqTabService.clientEntity = val;
        this.form.get('entity').setValue(val);
        this.formChange();
        this.form.updateValueAndValidity();
        if (this.enableMultipleHedges) {
          this.setHedgeTypeFromMode('Live');
          this.startCanSendRFQCheck();
        }
      }
    });
  }

  /**
   * Create a stub form in case form methods called before it's properly initialised.
   */
  private createDummyForm() {
    let ctrls: Record<string, FormControl | FormGroup> = {
      account: new FormControl('', [Validators.required]),
      entity: new FormControl('-'),
      primeBroker: new FormControl('', [Validators.required]),
      premium: new FormControl('', [Validators.required]),
      premiumValue: new FormControl('', [Validators.required]),
      premiumDate: new FormControl(''),
      hedge: new FormControl('', [Validators.required]),
      hedgeMode: new FormControl<HedgeMode>('Live'),
      hedgeRate: new FormControl(''),
      hedgeAmount: new FormControl(''),
      hedgeCustomAmount: new FormControl(''),
      hedgeDirection: new FormControl(''),
      hedgeDirectionBid: new FormControl(''),
      banks: new FormControl([]),
      isBlockTrade: new FormControl<boolean>(null),
      spots: new FormGroup({
        spotBid: new FormControl('', [Validators.required]),
        spotAsk: new FormControl('', [Validators.required])
      }),
      subAccount: new FormControl(
        {
          value: null,
          disabled: true
        },
        Validators.required
      )
    };

    this.form = new FormGroup(ctrls);
  }

  private createForm(): void {
    this.form = this.rfqSetupService.setupForm;
    this.setupSubscriptions.sink = this.form.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => {
          let va: any, vb: any;
          {
            let { premium, premiumValue, premiumDate } = a;
            va = { premium, premiumValue, premiumDate };
          }
          {
            let { premium, premiumValue, premiumDate } = b;
            vb = { premium, premiumValue, premiumDate };
          }
          return isEqual(va, vb);
        })
      )
      .subscribe(() => {
        this.rfqTabService.changeInHedges.next();
      });
  }

  private clearSubAccount(): void {
    const subAccountCtrl = this.form.get('subAccount');
    subAccountCtrl?.setValue(null);
    this.subAccountSelected = null;
    this.rfqTabService.selectedSubAccount = undefined;
  }

  private setupFormListeners() {
    const subAccountCtrl = this.form.get('subAccount');
    this.setupSubscriptions.sink = this.form
      .get('account')
      .valueChanges.subscribe(val => this.onAccountChange(val));

    this.setupSubscriptions.sink = this.form
      .get('hedge')
      .valueChanges.subscribe(val => this.onHedgeChange(val));

    this.setupSubscriptions.sink = this.form
      .get('premiumValue')
      .valueChanges.subscribe(val => this.onPremiumValueListenerChange(val));

    this.setupSubscriptions.sink = this.form
      .get('primeBroker')
      .valueChanges.subscribe(_ => this.onPrimeBrokerChange());

    this.setupSubscriptions.sink = this.form
      .get('entity')
      .valueChanges.subscribe(val => this.onEntityChange(val));

    this.setupSubscriptions.sink = this.form
      .get('premium')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(val => this.onPremiumChange(val));

    this.setupSubscriptions.sink = this.form
      .get('banks')
      .valueChanges.pipe(skip(this.rfqSetupService.accountSet() ? 1 : 0))
      .subscribe(val => this.onBanksChange(val));

    this.setupSubscriptions.sink = this.form
      .get('spots')
      ?.statusChanges.pipe(distinctUntilChanged(), startWith(null), pairwise())
      .subscribe(([previousValue, currentValue]) =>
        this.onSpotsChange(previousValue, currentValue)
      );

    this.setupSubscriptions.sink = this.form
      .get('execution')
      ?.valueChanges.subscribe({
        next: val => {
          this.rfqSetupService.changeInExecution(val);
          this.setLatestSpotRef();
        }
      });

    this.spotListener('spotAsk');
    this.spotListener('spotBid');
    this.HedgeRateListeners('hedgeRateAsk');
    this.HedgeRateListeners('hedgeRateBid');

    this.form
      .get('isDx')
      ?.valueChanges.subscribe(val =>
        this.rfqSetupService.enableHedgeRate(this.rfqTab.executionContext, val)
      );

    this.setupSubscriptions.sink = this.form
      .get('hedgeMode')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((curr: HedgeMode) => this.onHedgeModeChange(curr));

    this.setupSubscriptions.sink = this.form
      .get('premiumStyle')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(_ => this.onPremiumStyleChange());

    this.setupSubscriptions.sink = this.form
      .get('isBlockTrade')
      ?.valueChanges.subscribe(val => this.onIsBlockTradeChange(val));

    this.setupSubscriptions.sink = this.form
      .get('hedgeRates')
      ?.get('hedgeRateMid')
      ?.valueChanges.subscribe(_ => {
        this.isQuoteOutdated();
      });

    this.setupSubscriptions.sink = this.form
      .get('spotRefMethod')
      ?.valueChanges.subscribe(_ => {
        const hedgeValue = this.form.get('hedge').value;
        if (
          (hedgeValue === 'Forward Hedge' || hedgeValue === 'Custom Hedge') &&
          !this.hideMultipleHedges
        ) {
          this.multiHedgeGroups?.forEach(multiHedge => {
            multiHedge.hedgeComponents.forEach(hedge => {
              if (hedge.form.get('executionMode')) {
                hedge.resetHedgeRateSettings(
                  hedge.form.get('executionMode').value,
                  false
                );
              }
            });
          });
        }
        this.submitSetupEventGenericMultiHedge('spotRefMethod');
      });

    this.setupSubscriptions.sink = subAccountCtrl?.valueChanges.subscribe(val =>
      this.onSubAccountChange(val)
    );

    this.rfqSetupService.genericAttributeListener();

    this.setupSubscriptions.sink = this.form
      .get('bookingLocation')
      ?.valueChanges.subscribe(val => this.onBookingLocationChange(val));
  }

  private setLatestSpotRef(): void {
    this.rfqSetupService
      .setLatestRefs(this.rfqTab, this.legExpiryDates)
      .subscribe({
        next: _ => {
          this.submitSetupEventGenericMultiHedge('hedgeRate');
        }
      });
  }

  private onBookingLocationChange(val: string): void {
    const attr = this.additionalRfqAttributes()?.find(v => {
      return v.shortName === 'bookingLocation';
    });
    if (!attr) {
      return;
    }
    const tests = attr.forcePrimeBrokerToNoneExceptOnValues;
    if (!tests || tests.length === 0) {
      return;
    }
    this.onPrimeBrokerDisabled = 'DISABLED';
    const primeBrokerCtrl = this.form.get('primeBroker');
    if (tests.includes(val)) {
      primeBrokerCtrl.enable();
    } else {
      primeBrokerCtrl.setValue('None');
      primeBrokerCtrl.disable();
    }
  }

  @Throttle(250)
  public primeBrokerVisible(): boolean {
    if (this.onPrimeBrokerDisabled === 'HIDE') {
      return !this.form?.controls['primeBroker'].disabled;
    } else {
      return true;
    }
  }

  public submitSetupEventGenericMultiHedge(change: SetupEventChange): void {
    const hedge: Hedges = this.form.get('hedge')?.value;
    if (
      !(hedge === 'Forward Hedge' || hedge === 'Spot Hedge') ||
      (this.multiHedgeGroups?.first?.hedgeRatesPending() &&
        !this.hideMultipleHedges)
    ) {
      return;
    }

    if (this.form.valid && this.rfqRunning) {
      this.submitSetupEvent(change);
    }
  }

  private onAccountChange(val: string): void {
    this.getNewPrimeBrokers(false);
    this.rfqSetupService.updateAccountSet(val);
    this.clearSubAccount();
    if (this.form.get('isBlockTrade')?.value === 'No' && val) {
      this.getSubAccounts();
    }
    this.updatePanelTitleAccount();
  }

  private onHedgeChange(val: Hedges) {
    this.hedgeRateStatus(val);
    this.hedgeChange.emit(val);
    this.disableAdditionalRFQAttributes(val);
    this.marketRates = null;
    this.enableCustomHedgeAmount =
      this.rfqTabService.tabData.executionContext.enableCustomHedgeAmount &&
      val !== 'None';
    if (this.rfqTabService.executionContextType() === 'genericMultiHedge') {
      this.enableMultipleHedges =
        val === 'Forward Hedge' || val === 'Custom Hedge';
      this.hideMultipleHedges = false;
      this.setShowIndicativeSpot();
      this.setShowSoleProviderSpot();

      if (val === 'Custom Hedge') {
        this.form.get('spotRefMethod').enable();
        this.form.get('execution').disable({ emitEvent: false });
        return;
      }

      if (val === 'Spot Hedge' || val === 'Forward Hedge') {
        this.setLatestSpotRef();
        if (val === 'Forward Hedge') {
          this.enableHedgeRatesForForwardHedge();
        } else {
          this.form.get('hedgeRates').enable();
          this.form.get('spotRefMethod').enable();
          this.form.get('execution').enable();
        }

        if (this.form.get('hedgeRates').enabled) {
          if (this.form.get('execution').value === 'Live') {
            this.form.get('hedgeRates').get('hedgeRateAsk').enable();
            this.form.get('hedgeRates').get('hedgeRateBid').enable();
            this.form.get('hedgeRates').get('hedgeRateMid').disable();
          } else {
            this.form.get('hedgeRates').get('hedgeRateAsk').disable();
            this.form.get('hedgeRates').get('hedgeRateBid').disable();
            this.form.get('hedgeRates').get('hedgeRateMid').enable();
          }
        }
      } else {
        this.form.get('execution').disable();
        this.form.get('hedgeRates').disable();
        this.form.get('spotRefMethod').disable();
      }
    }
  }

  private disableAdditionalRFQAttributes(val: string) {
    if (!this.additionalRfqAttributes()) {
      return;
    }
    this.additionalRfqAttributes().forEach(attr => {
      const ctrl = this.form.get(attr.shortName);
      if (ctrl) {
        if (val === 'None' && attr.disableOnNoneHedgeType) {
          ctrl.disable();
        } else {
          ctrl.enable();
        }
      }
    });
  }
  private onPremiumValueListenerChange(val: string) {
    if (this.legSyncingInfo?.premiumValueType) {
      this.rfqTabService.selectedLegs?.forEach((_, index: number) => {
        this.legSyncService.syncPremiumType(index, val);
      });
    }
    this.formChange(val);
  }

  private onPrimeBrokerChange() {
    if (!this.useHydraProviders) {
      this.getProviders();
    }
  }

  private spotListener(field: SpotPosition): void {
    this.setupSubscriptions.sink = this.form
      .get('spots')
      ?.get(field)
      .valueChanges.subscribe(val =>
        this.onTwoWayChange(field, val.toString(), 'spots')
      );
  }

  private HedgeRateListeners(field: 'hedgeRateAsk' | 'hedgeRateBid'): void {
    const hedgeRates = this.form.get('hedgeRates');
    this.setupSubscriptions.sink = hedgeRates
      ?.get(field)
      .valueChanges.subscribe(val => {
        if (this.rfqTab.executionContext.enableTwoWayCustomHedgeRateForNonDx) {
          this.onTwoWayChange(field, val.toString(), 'hedgeRates', true);
        }
        this.isQuoteOutdated();
      });
  }

  private isQuoteOutdated(): void {
    if (this.rfqTabService.executionContextType() === 'genericMultiHedge') {
      [
        ...this.rfqTabService.quotes.bids,
        ...this.rfqTabService.quotes.offers
      ].forEach(quote =>
        isQuoteOutdated(
          quote,
          this.form.getRawValue(),
          this.rfqSetupService.setup?.getRawHedges()
        )
      );
    }
  }

  private onEntityChange(val: string): void {
    this.rfqTabService.clientEntity = val;
  }

  private onPremiumChange(val: string): void {
    if (this.rfqTabService.tabData.isVolBased) {
      this.rfqTabService.selectedLegs?.forEach(legComponent => {
        legComponent.form.get('rfqPremiumMargin').updateValueAndValidity();
      });
    }
    this.premiumChange.emit(val);
    setTimeout(() => {
      this.setPremiumStyle(
        val,
        !this.form.get('premiumStyle')?.dirty &&
          !!this.rfqTab.presetSetupData?.setup.premiumStyle
      );
    }, 0);
  }

  private onBanksChange(val: { shortName: string }[]): void {
    const shortNames = val.map(provider => provider.shortName);
    if (!isEqual(this.previousProvidersEmitted, shortNames)) {
      this.providersChange.emit(shortNames);
      this.previousProvidersEmitted = shortNames;
    }

    this.setShowSoleProviderSpot(val);
  }

  private onSpotsChange(
    previousValue: FormControlStatus,
    currentValue: FormControlStatus
  ): void {
    const changeColor =
      currentValue === 'DISABLED' || previousValue === 'DISABLED';
    if (changeColor) {
      const color = currentValue === 'DISABLED' ? greyColor : null;
      this.updateSpotColor('spotAsk', color);
      this.updateSpotColor('spotBid', color);
    }
  }

  private onHedgeModeChange(curr: HedgeMode): void {
    this.setHedgeTypeFromMode(curr);
    this.rfqTabService.handleClientQuoteEventUsingLast('hedgeMode');
    if (
      this.rfqTabService.tabData.isVolBased &&
      this.rfqTabService.tabData.executionContext.useSpotInRfqPanel &&
      curr !== 'DX' &&
      curr !== 'Live'
    ) {
      this.form.get('spots').enable();
    }
  }

  private onIsBlockTradeChange(val: string): void {
    const subAccountCtrl = this.form.get('subAccount');
    this.clearSubAccount();
    if (val === 'No') {
      subAccountCtrl.enable();
      if (this.form.get('account').value) {
        this.getSubAccounts();
      }
    } else {
      subAccountCtrl.disable();
    }
  }

  private onPremiumStyleChange(): void {
    setTimeout(() => {
      this.rfqTabService.reBuildQuotes();
    }, 0);
  }

  private onSubAccountChange(val: string): void {
    this.subAccountCID = this.subAccounts.find(
      x => x['Client Short Name'] === val
    )?.['Account CID'];

    this.onSelectSubAccount(
      this.subAccounts.findIndex(x => x['Account Short Name'] === val)
    );
  }

  private onTwoWayChange(
    field: string,
    val: string,
    groupField: string,
    formatNumber = false
  ): void {
    if (val.toString().toLowerCase().includes('ch')) {
      this.handleChoicePrice(field, val, groupField, formatNumber);
    }
  }

  private getSubAccounts(): void {
    this.subAccounts = [];
    this.subAccountCID = null;
    const account = this.form.get('account').value;
    this.form.get('subAccount').markAsPending();

    this.rfqService.getSubAccounts(account).subscribe({
      next: (response: SubAccount[]) => {
        this.subAccounts = response;
        this.form.get('subAccount').updateValueAndValidity();
      },
      error: _ => {
        this.form.get('subAccount').updateValueAndValidity();
      }
    });
  }

  private updateSpotColor(field: SpotPosition, newColor?: string): void {
    const liveSpotColorsCopy = clone(this.liveSpotColors);
    const currentColor = liveSpotColorsCopy[field].current;
    liveSpotColorsCopy[field].current =
      newColor || liveSpotColorsCopy[field].previous;
    liveSpotColorsCopy[field].previous = currentColor;
    this.liveSpotColors = liveSpotColorsCopy;
  }

  private setPremiumStyle(premium: string, blockSetValue = false): void {
    if (!this.form.get('premiumStyle')) {
      return;
    }
    const quotePair = this.rfqTabService.tabData.title.quotePair;

    if (
      !blockSetValue &&
      this.isVolBasedDevUat &&
      quotePair.base !== premium &&
      quotePair.term !== premium
    ) {
      this.premiumStyles = ['Amount'];
      this.form.get('premiumStyle').setValue('Amount');
      return;
    }
    const isPremiumStyleBase = premium === quotePair.base;
    this.premiumStyles = isPremiumStyleBase
      ? premiumStylesIfPremiumBase
      : premiumStylesIfPremiumTerm;

    if (!blockSetValue) {
      this.form
        .get('premiumStyle')
        .setValue(isPremiumStyleBase ? 'bps' : 'pips');
    }
  }

  @Throttle(1000)
  public getHedgeModes(): HedgeMode[] {
    if (this.rfqRunning) {
      const mode = this.form.get('hedgeMode').value;
      if (mode === 'Live' || mode === 'DX') {
        return ['Live', 'DX'];
      } else {
        return ['None'];
      }
    } else {
      return ['Live', 'DX', 'None'];
    }
  }

  private setHedgeTypeFromMode(val: HedgeMode, blockSetValue = false): void {
    if (!this.rfqTabService.tabData.title.quotePair) {
      return;
    }

    if (val === 'None') {
      this.rfqSetupService.updateHedges(hedgeTypesIfModeIsNone);
    } else {
      if (this.rfqTabService.isMedusaDeliverable()) {
        this.rfqSetupService.updateHedges(hedgeTypesIfMedusaDeliverable);
        this.setDefaultHedges();
      } else {
        this.rfqSetupService.updateHedges(hedgeTypesIfMedusaNonDeliverable);
      }
    }
    if (!blockSetValue) {
      this.form.get('hedge').setValue(this.hedges()[0], { emitEvent: false });
    }
  }

  private setDefaultHedges(): void {
    setTimeout(_ => {
      const hedgesComponents = this.multiHedgeGroups.first;
      if (hedgesComponents) {
        const nOfHedges = hedgesComponents.getHedges()?.length;
        if (!nOfHedges) {
          hedgesComponents.addHedge();
        }
      }
    }, 0);
  }

  private calculateSpotDate() {
    const premiumDateCtrl = this.form.get('premiumDate');
    premiumDateCtrl.markAsPending();
    this.rfqTabService.calculateSpotDate().subscribe(
      formattedDate => {
        premiumDateCtrl.setValue(formattedDate);
        this.setLegsPremiumDates(formattedDate);
      },
      () => {
        premiumDateCtrl.setValue('');
      }
    );
  }

  private calculateForwardDate() {
    const d = this.rfqTabService.tabData.legs[0]?.leg.settlementDate;
    if (!d) {
      return;
    }

    this.form.get('premiumDate').setValue(reformatDate(d, 'D/M/YYYY'));

    setTimeout(() => {
      this.form.get('premiumDate').setValue(reformatDate(d, 'D MMM YYYY'), {
        emitEvent: false
      });
    }, 100);
  }

  private formChange(val?: string) {
    if (val == null) {
      val = this.form.get('premiumValue').value;
    }

    if (val === 'Spot') {
      this.calculateSpotDate();
    } else if (val === 'Forward') {
      this.calculateForwardDate();
    }
    this.onPremiumValueChange(val);
  }

  private initHedgeDirections(blockSetValue = false): void {
    if (!blockSetValue) {
      if (this.optionType === 'Call') {
        this.form.get('hedgeDirection').setValue('Sell');
        this.form.get('hedgeDirectionBid').setValue('Buy');
      } else {
        this.form.get('hedgeDirection').setValue('Buy');
        this.form.get('hedgeDirectionBid').setValue('Sell');
      }
      this.form.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.directionListenedTo) {
      this.setupSubscriptions.sink = this.form
        .get('hedgeDirection')
        .valueChanges.subscribe(() => {
          this.setupHedgeDirections();
        });
      this.directionListenedTo = true;
    }
  }

  private setupHedgeDirections(using: 'Bid' | '' = '') {
    const fromItem = 'hedgeDirection' + using;
    const toItem = 'hedgeDirection' + (using === 'Bid' ? '' : 'Bid');
    if (this.form.get(fromItem).value === 'Buy') {
      this.form.get(toItem).setValue('Sell', { emitEvent: false });
    } else {
      this.form.get(toItem).setValue('Buy', { emitEvent: false });
    }
    this.form.updateValueAndValidity();
  }

  private depositCurrency(): string {
    return this.rfqTab.static
      ? this.rfqTabService.tabData.legs[0].leg.depositCurrency
      : this.rfqTabService.selectedLegs[0].form.get('depositCurrency').value;
  }

  @Throttle(250)
  public getHedges() {
    if (!this.enableMultipleHedges) {
      return { hedges: [] };
    }
    const hedgeGroups = this.multiHedgeGroups.toArray();
    if (hedgeGroups.length === 0) {
      return { hedges: [] };
    }
    if (hedgeGroups.length !== 1) {
      throw new Error('Expected 1 hedge group');
    }

    const hedges = mapHedgeGroup(hedgeGroups[0].getHedges());
    return { hedges };
  }

  public getRawHedges() {
    if (!this.enableMultipleHedges) {
      return [];
    }
    const hedgeGroups = this.multiHedgeGroups.toArray();
    if (hedgeGroups.length === 0) {
      return [];
    }

    return hedgeGroups[0].getRawHedges();
  }

  private handleChoicePrice(
    fieldName: string,
    value: string,
    groupField: string,
    formatNumber = false
  ): void {
    const cross = this.currencyPair.base + this.currencyPair.term;
    const spotsGroup = this.form.get(groupField) as FormGroup;
    const cleanValue = Utilities.formattedNumberToNumber(value);
    const newValue = Utilities.roundNumberToDp(
      cleanValue,
      this.productsConfigService.decimalPlacesForCross(cross),
      formatNumber
    );

    if (
      handleChoicePrice(
        spotsGroup,
        fieldName,
        !isNaN(cleanValue) ? newValue.toString() : ''
      )
    ) {
      const correspondingSide = getOppositePosition(fieldName);
      if (groupField === 'spots') {
        this.spotFieldEdited(correspondingSide as SpotPosition);
      }
    }
  }

  private updatePanelTitleAccount(): void {
    (this.rfqTabService.tabData as RfqTab).title.account =
      this.form.get('account').value;
  }

  private setProviders(providers: Provider[]): void {
    this.rfqTabService.providers = providers;
    this.providers = providers;

    if (providers.length === 1) {
      setTimeout(() => {
        this.selectedBanksState.set(providers);
      }, 0);
    }

    this.numOfProvidersAvailable.emit(providers.length);
  }

  private setShowIndicativeSpot(): void {
    const hedge = this.form.get('hedge')?.value;

    if (
      this.rfqTabService.executionContextType() === 'genericMultiHedge' &&
      (hedge === 'Live Auto Hedger' || hedge === 'Spot Hedge')
    ) {
      this.displayIndicativeSpot = false;
      return;
    }

    this.displayIndicativeSpot =
      this.configService.userSettings().displayReadOnlySpotInRfqPanel ||
      (this.adminService.userInfo.isWhiteLabelUser &&
        !this.rfqTabService.tabData.executionContext.useSpotInRfqPanel);
  }

  private setShowSoleProviderSpot(banks?: { shortName: string }[]): void {
    const hedge = this.form.get('hedge')?.value;

    if (
      this.rfqTabService.executionContextType() === 'genericMultiHedge' &&
      hedge === 'Spot Hedge'
    ) {
      this.showSoleProviderSpot = false;
      return;
    }

    banks = banks || this.form.get('banks').value;
    const showSoleProviderQuotes =
      !!this.configService.userSettings().parent
        .rfqDisplaySpotFromSoleProviderQuotes;
    this.showSoleProviderSpot = showSoleProviderQuotes && banks.length === 1;
  }
}
